/* eslint-disable @typescript-eslint/no-explicit-any */
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { wrappingInputRule } from '@tiptap/core';
import { BulletList } from '@tiptap/extension-bullet-list';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { marked } from 'marked';
import React, { useEffect, useState } from 'react';
import theme from 'styles/theme';
import './textEditor.css';

export const CustomBulletList = BulletList.extend({
  addInputRules() {
    return [
      wrappingInputRule({
        find: /^\s*([-+*])\s$/,
        type: this.type,
      }),
    ];
  },
});

marked.setOptions({
  breaks: true, // Treat single line breaks as <br>
});

export function convertHtmlStringToText(htmlString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const lines: string[] = [];
  function processNode(node: any, listType: 'ul' | 'ol' | null = null, index = 1): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').trim();
      if (text) {
        if (listType === 'ul') lines.push(`- ${text}`);
        else if (listType === 'ol') lines.push(`${index}. ${text}`);
        else lines.push(text);
      }
      return;
    }

    if (node.nodeName === 'LI') {
      const paragraphs: any[] = Array.from(node.querySelectorAll('p'));
      if (paragraphs.length === 0) {
        const text = (node.textContent || '').trim();
        if (text) {
          lines.push(listType === 'ol' ? `${index}. ${text}` : `• ${text}`);
        }
      } else {
        paragraphs.forEach((p: any, idx: number) => {
          const text = (p.textContent || '').trim();
          if (text) {
            if (listType === 'ol' && idx === 0) {
              lines.push(`${index}. ${text}`);
            } else if (listType === 'ul' && idx === 0) {
              lines.push(`• ${text}`);
            } else {
              lines.push(text);
            }
          } else {
            lines.push(''); // Empty <p> adds a blank line
          }
        });
      }
    } else if (node.nodeName === 'P') {
      const text = (node.textContent || '').trim();
      lines.push(text || '');
    } else if (node.nodeName === 'UL') {
      Array.from(node.children).forEach((li: any) => processNode(li, 'ul'));
    } else if (node.nodeName === 'OL') {
      Array.from(node.children).forEach((li: any, i: number) => processNode(li, 'ol', i + 1));
    } else {
      node.childNodes.forEach((child: any) => processNode(child, listType, index));
    }
  }
  doc.body.childNodes.forEach((node: any) => processNode(node));
  return lines.join('\n');
}

interface RichTextEditorProps {
  value: string;
  onChange?: (value: string) => void;
  isReadOnly?: boolean;
  showToolbar?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active: boolean;
  icon: React.ReactNode;
  title: string;
}

const ToolbarButton = ({ onClick, active, icon, title }: ToolbarButtonProps) => (
  <button
    onMouseDown={e => {
      e.preventDefault();
      onClick();
    }}
    className={`toolbar-button${active ? ' active' : ''}`}
    title={title}
    type="button"
    aria-pressed={active}
  >
    {icon}
  </button>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange = () => {},
  isReadOnly = false,
  showToolbar = true,
}) => {
  const [initialContent] = useState(value);

  const editor = useEditor({
    editable: !isReadOnly,
    extensions: [StarterKit.configure({ bulletList: false, codeBlock: false, code: false }), CustomBulletList],
    content: initialContent,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // Remove <pre> and <code> blocks before pasting
      transformPastedHTML: html => html.replace(/<\/?(pre|code)[^>]*>/gi, ''),
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter') {
          // Allow only *plain* Enter (no modifiers)
          if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
            event.preventDefault();
            return true; // block it
          }
        }
        return false; // Let other keys pass
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div
      id="Edit-Summary-Textarea"
      data-automation="Edit-Summary-Textarea"
      className={`tiptap-editor-container ${isReadOnly ? 'readonly' : ''}`}
    >
      {showToolbar && !isReadOnly && (
        <div className="toolbar">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            icon={<FormatBoldIcon />}
            title="Bold"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            icon={<FormatListBulletedIcon />}
            title="Bullet List"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            icon={<FormatListNumberedIcon />}
            title="Ordered List"
          />
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().unsetAllMarks().clearNodes().run();
            }}
            active={false}
            icon={<FormatClearIcon />}
            title="Clear Formatting"
          />
        </div>
      )}
      <EditorContent
        editor={editor}
        className={`editor-content ${isReadOnly ? 'readonly' : ''}`}
        spellCheck={!isReadOnly}
        style={{ color: theme.colorPrimaryPrimary100 }}
      />
    </div>
  );
};

export default RichTextEditor;
